(() => {
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const desktop = matchMedia('(min-width: 1000px)');
  const hover = matchMedia('(hover: hover) and (min-width: 1000px)');
  const panels = new Set();
  const active = new Set();
  const galleries = new Set();
  const initialised = new WeakSet();
  let frame = 0;
  const clamp = value => Math.min(1, Math.max(0, value));
  const editor = () => Boolean(window.Shopify?.designMode);
  const reveal = new IntersectionObserver(entries => {
    entries.forEach(({target, isIntersecting}) => {
      if (!isIntersecting) return;
      target.classList.remove('mw-pending');
      target.classList.add('mw-visible');
      reveal.unobserve(target);
    });
  }, {threshold:0.04,rootMargin:'0px 0px 70px 0px'});
  const visibility = new IntersectionObserver(entries => {
    entries.forEach(({target,isIntersecting}) => isIntersecting ? active.add(target) : active.delete(target));
    schedule();
  }, {rootMargin:'100px'});
  function configureScene(hero) {
    const scene = hero.closest('[data-mw-scene]');
    if (!scene) return;
    scene.classList.remove('mw-scene-ready');
    if (scene.dataset.mwScene !== 'true' || reduced.matches || !desktop.matches || editor() || hero.dataset.mwMotion !== 'true') return;
    scene.style.setProperty('--mw-hero-height', `${Math.ceil(hero.getBoundingClientRect().height)}px`);
    scene.classList.add('mw-scene-ready');
  }
  function update() {
    frame = 0;
    if (reduced.matches || editor()) return;
    active.forEach(panel => {
      const rect = panel.getBoundingClientRect();
      if (panel.hasAttribute('data-mw-hero')) {
        const scene = panel.closest('[data-mw-scene]');
        const bounds = scene?.classList.contains('mw-scene-ready') ? scene.getBoundingClientRect() : rect;
        const runway = Math.max(1, bounds.height - rect.height);
        const progress = clamp(-bounds.top / (runway > 10 ? runway : rect.height));
        panel.style.setProperty('--mw-hero-zoom', String(1 + progress * Number(panel.dataset.mwStrength || 8) / 100));
        panel.style.setProperty('--mw-hero-drift', `${-progress * (desktop.matches ? 28 : 8)}px`);
        const end = clamp((progress - .6) / .4);
        const radius = parseFloat(getComputedStyle(panel).getPropertyValue('--mw-hero-radius')) || 0;
        panel.style.setProperty('--mw-frame', `${end * radius}px`);
        panel.style.setProperty('--mw-hero-inset', `${desktop.matches ? end * 14 : 0}px`);
        return;
      }
      const progress = clamp((innerHeight - rect.top) / (innerHeight + rect.height));
      panel.style.setProperty('--mw-editorial-zoom', String(1.07 - progress * .07));
      panel.style.setProperty('--mw-story-zoom', String(1.05 - progress * .05));
      panel.style.setProperty('--mw-sunrise-scale', String(.94 + progress * .12));
      panel.style.setProperty('--mw-sunrise-lift', `${(1 - progress) * 12}px`);
      panel.style.setProperty('--mw-line-progress', String(clamp(progress * 2)));
      panel.style.setProperty('--mw-editorial-inset', `${desktop.matches ? (1 - progress) * 20 : 0}px`);
    });
  }
  function schedule() { if (!frame) frame = requestAnimationFrame(update); }
  function setupGallery(panel) {
    if (panel.dataset.mwGallery !== 'true') return;
    const grid = panel.querySelector('[class*="__grid"]');
    const cards = Array.from(panel.querySelectorAll('[data-mw-gallery-card]'));
    if (!grid || cards.length < 2) return;
    const reset = () => grid.style.removeProperty('grid-template-columns');
    const expand = card => {
      if (!hover.matches || reduced.matches || editor() || panel.dataset.mwMotion !== 'true') return;
      if (getComputedStyle(grid).gridTemplateColumns.split(' ').length !== cards.length) return;
      grid.style.gridTemplateColumns = cards.map(item => item === card ? 'minmax(0,1.45fr)' : 'minmax(0,1fr)').join(' ');
    };
    cards.forEach(card => {
      card.addEventListener('mouseenter', () => expand(card));
      card.addEventListener('focusin', () => expand(card));
    });
    grid.addEventListener('mouseleave', reset);
    grid.addEventListener('focusout', event => { if (!grid.contains(event.relatedTarget)) reset(); });
    panel.mwResetGallery = reset;
    galleries.add(panel);
  }
  function init(root = document) {
    root.querySelectorAll('[data-mw-hero],[data-mw-panel]').forEach(panel => {
      if (initialised.has(panel)) return;
      initialised.add(panel);
      panels.add(panel);
      if (panel.hasAttribute('data-mw-hero')) configureScene(panel);
      if (panel.dataset.mwKind === 'gallery') setupGallery(panel);
      if (reduced.matches || editor() || panel.dataset.mwMotion !== 'true') return;
      visibility.observe(panel);
      if (panel.hasAttribute('data-mw-hero')) return;
      const candidates = panel.querySelectorAll('[class*="__heading"],[class*="__body"],[class*="__card"],[class*="__item"],[class*="__media"]');
      let index = 0;
      candidates.forEach(element => {
        if (element.parentElement.closest('[class*="__card"],[class*="__item"]')) return;
        element.classList.add('mw-reveal');
        element.style.setProperty('--mw-delay', `${(index++ % 3) * 65}ms`);
        if (element.getBoundingClientRect().top > innerHeight + 40) {
          element.classList.add('mw-pending');
          reveal.observe(element);
        }
      });
    });
    schedule();
  }
  function resetMotion() {
    panels.forEach(panel => {
      ['--mw-hero-zoom','--mw-hero-drift','--mw-frame','--mw-hero-inset','--mw-editorial-zoom','--mw-story-zoom','--mw-sunrise-scale','--mw-sunrise-lift','--mw-line-progress','--mw-editorial-inset'].forEach(key => panel.style.removeProperty(key));
      panel.querySelectorAll('.mw-pending').forEach(element => element.classList.remove('mw-pending'));
      if (panel.hasAttribute('data-mw-hero')) configureScene(panel);
      if (!reduced.matches && !editor() && panel.dataset.mwMotion === 'true') visibility.observe(panel);
    });
    galleries.forEach(panel => panel.mwResetGallery());
    schedule();
  }
  window.addEventListener('scroll', schedule, {passive:true});
  let resizeTimer;
  window.addEventListener('resize', () => {
    schedule();
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => {
      panels.forEach(panel => { if (panel.hasAttribute('data-mw-hero')) configureScene(panel); });
      schedule();
    }, 150);
  }, {passive:true});
  window.addEventListener('load', () => { panels.forEach(panel => { if (panel.hasAttribute('data-mw-hero')) configureScene(panel); }); schedule(); }, {once:true});
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => {
    event.target.querySelectorAll('[data-mw-panel],[data-mw-hero]').forEach(panel => {
      panels.delete(panel);active.delete(panel);galleries.delete(panel);visibility.unobserve(panel);
      panel.querySelectorAll('.mw-reveal').forEach(element => reveal.unobserve(element));
    });
  });
  document.addEventListener('shopify:block:select', event => event.target.closest('[data-mw-panel]')?.querySelectorAll('.mw-pending').forEach(element => element.classList.remove('mw-pending')));
  reduced.addEventListener('change', resetMotion);
  desktop.addEventListener('change', resetMotion);
  hover.addEventListener('change', () => galleries.forEach(panel => panel.mwResetGallery()));
  init();
})();
